import React from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { useCad } from "../context/CadContext";
import { DEFAULT_SETTINGS } from "../constants/defaults";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";

const FIELDS = [
  { key: "pegSize", label: "Female peg size", min: 1, max: 12, step: 0.1, unit: "mm" },
  { key: "tolerance", label: "Tolerance (inward / side)", min: 0.01, max: 0.5, step: 0.01, unit: "mm" },
  { key: "maleHeight", label: "Male peg height", min: 0.4, max: 5, step: 0.1, unit: "mm" },
  { key: "femaleExtra", label: "Female extra depth", min: 0, max: 2, step: 0.05, unit: "mm" },
  { key: "chamfer", label: "Chamfer", min: 0, max: 0.5, step: 0.01, unit: "mm" },
  { key: "thickness", label: "Default plate thickness", min: 0.5, max: 10, step: 0.5, unit: "mm" },
];

export function SettingsDialog({ open, onOpenChange }) {
  const { settings, setSettings } = useCad();
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Global Snap-Fit Settings</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Defaults for every new connector. Per-connector overrides live in the properties panel.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Peg shape</span>
            <div className="flex gap-2 mt-1.5">
              {["square", "diamond"].map((s) => (
                <button
                  key={s}
                  data-testid={`settings-shape-${s}`}
                  onClick={() => set("pegShape", s)}
                  className={`flex-1 h-9 rounded-lg text-xs capitalize font-medium transition-colors duration-150 ${
                    settings.pegShape === s
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      : "bg-slate-950/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>

          {FIELDS.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-300">{f.label}</span>
                <span className="font-mono text-xs text-emerald-400" data-testid={`settings-${f.key}-value`}>
                  {Number(settings[f.key]).toFixed(2)} {f.unit}
                </span>
              </div>
              <Slider
                data-testid={`settings-${f.key}-slider`}
                value={[settings[f.key]]}
                min={f.min}
                max={f.max}
                step={f.step}
                onValueChange={([v]) => set(f.key, +v.toFixed(2))}
              />
            </div>
          ))}

          <div className="text-[11px] font-mono text-slate-500 bg-slate-950/50 rounded-lg p-2.5 border border-slate-800 grid grid-cols-2 gap-1">
            <span>Male size: <span className="text-sky-400">{(settings.pegSize - 2 * settings.tolerance).toFixed(2)}mm</span></span>
            <span>Female depth: <span className="text-rose-400">{(settings.maleHeight + settings.femaleExtra).toFixed(2)}mm</span></span>
          </div>
        </div>

        <Button
          data-testid="settings-reset-btn"
          variant="outline"
          onClick={() => { setSettings(DEFAULT_SETTINGS); toast.success("Settings reset to defaults"); }}
          className="gap-2 border-slate-700 bg-slate-950/50 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <RotateCcw size={14} /> Reset to defaults
        </Button>
      </DialogContent>
    </Dialog>
  );
}
